
import { Component, Ref, Vue } from 'nuxt-property-decorator'
import type { MetaInfo } from 'vue-meta'
import { Swiper, SwiperCore, SwiperSlide } from 'swiper-vue2'
import { Navigation } from 'swiper'
import { Context } from '@nuxt/types'
import MainBanner from '~/components/MainBanner/MainBanner.vue'
import Partners from '~/components/sections/Partners.vue'
import Cards from '~/components/sections/how-it-works/CardsGrid.vue'
import CardsItem from '~/components/sections/how-it-works/CardsGridItem.vue'
import AppFullPageLoader from '~/components/AppFullPageLoader.vue'
import BestDealGuarantee from '~/components/widgets/BestDealGuarantee.vue'
import B2bFormModal from '~/components/modals/B2bFormModal.vue'
import { WithHead } from '~/core/vue.types'
import SaveRouteMixin from '~/mixins/save-route.mixin'
import B2bForm from '~/components/sections/forms/B2bForm.vue'
import B2bReview, { BusinessReview } from '~/components/b2b/B2bReview.vue'
import 'swiper/swiper-bundle.css'
import IconArrow from '~/assets/images/icons/icon-arrow-right-clear.svg?inline'
import { FeatureFlags } from '~/core/config/feature-flags'
import { AlButton } from '~/components/ui'
import { useSEOMetaComposer } from '~/composables/seo.composable'

SwiperCore.use([Navigation])

@Component({
  nuxtI18n: {
    locales: ['de'],
    paths: {
      de: '/handy-und-internet-abos-fuer-geschaeftskunden',
    },
  },
  components: {
    AlButton,
    MainBanner,
    Partners,
    Cards,
    CardsItem,
    AppFullPageLoader,
    BestDealGuarantee,
    B2bFormModal,
    B2bForm,
    B2bReview,
    Swiper,
    SwiperSlide,
    IconArrow,
  },
  mixins: [SaveRouteMixin],
  middleware: ({ error, $featureFlags }: Context) => {
    if (!$featureFlags.isEnabled(FeatureFlags.B2B_PAGE)) {
      error({ statusCode: 404 })
    }
  },
})
export default class BusinessPage extends Vue implements WithHead {
  @Ref('b2bCasesSlider')
  readonly b2bCasesSliderRef!: Vue

  isLoading = false
  showModal = false

  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.$t('head.meta.b2b.title').toString(),
        description: this.$t('head.meta.b2b.description').toString(),
        route: { name: 'b2b-index' },
      },
    )
  }

  get heroImages () {
    return [
      require('~/assets/images/b2b/hero.png'),
      require('~/assets/images/b2b/hero@2x.png'),
    ]
  }

  get heroImagesStr () {
    return `${this.heroImages[0]} 1x, ${this.heroImages[1]} 2x`
  }

  get infoImages () {
    return [
      require('~/assets/images/b2b/info.png'),
      require('~/assets/images/b2b/info@2x.png'),
    ]
  }

  get infoImagesStr () {
    return `${this.infoImages[0]} 1x, ${this.infoImages[1]} 2x`
  }

  get formImages () {
    return [
      require('~/assets/images/b2b/form-bg.png'),
      require('~/assets/images/b2b/form-bg@2x.png'),
    ]
  }

  get formImagesStr () {
    return `${this.formImages[0]} 1x, ${this.formImages[1]} 2x`
  }

  get calculatedSlides () {
    if (this.$mqUtils.isMobile) {
      return 1
    }
    if (this.$mqUtils.isMD) {
      return 2
    }

    return 3
  }

  get reviews (): BusinessReview[] {
    return [
      {
        logoSrc: require('~/assets/images/b2b/review-logo-1.png'),
        text: this.$t('b2b.reviews.items.1.text') as string,
        address: this.$t('b2b.reviews.items.1.address') as string,
        to: this.localePath('b2b-fallstudie-apartment-service'),
      },
      {
        logoSrc: require('~/assets/images/b2b/review-logo-2.png'),
        text: this.$t('b2b.reviews.items.2.text') as string,
        address: this.$t('b2b.reviews.items.2.address') as string,
        to: this.localePath('b2b-fallstudie-kern-studer'),
      },
      {
        logoSrc: require('~/assets/images/b2b/review-logo-3.png'),
        text: this.$t('b2b.reviews.items.3.text') as string,
        address: this.$t('b2b.reviews.items.3.address') as string,
        to: this.localePath('b2b-fallstudie-sanoptis'),
      },
    ]
  }

  nextSlide () {
    (this.b2bCasesSliderRef.$el as any).swiper.slideNext()
  }

  prevSlide () {
    (this.b2bCasesSliderRef.$el as any).swiper.slidePrev()
  }
}
